// @flow
import React, {useContext, useEffect, useState} from "react"
import LIUtils, {SAMPLE_TYPE_BLOCK, SAMPLE_TYPE_SLIDE} from "../GUI_COMMON/SUPPORT/LIUtils";
import Header from "./Header";
import BackButton from "../GUI_COMMON/COMPONENTS/BackButton";
import ArchiverClientApi from "../API/archiverClientApi";
import {useHistory, useLocation, useParams} from "react-router-dom";
import LoadingSpinner from "../GUI_COMMON/COMPONENTS/LoadingSpinner";
import SampleCard from "./SampleCard";
import ReloadButton from "../GUI_COMMON/COMPONENTS/ReloadButton";
import ArchiverGuiUtils from "../SUPPORT/ArchiverGuiUtils";
import {StoreContext} from "../GUI_COMMON/SUPPORT/store";
import GeneralMagView from "../GUI_COMMON/COMPONENTS/GeneralMagView";
import Popup from "../GUI_COMMON/COMPONENTS/Popup";
import SampleLegend from "../GUI_COMMON/COMPONENTS/SampleLegend";
import {useCookies} from "react-cookie";
import LIButton from "../GUI_COMMON/COMPONENTS/LIButton";

const MagDetailsPage = () => {
    
    const [globalState] = useContext(StoreContext);
    const [isLoading, setIsLoading] = useState(false);
    const [discardIsBusy, setDiscardIsBusy] = useState(false);
    const [isLoadingBSamples, setIsLoadingBSamples] = useState(false);
    const [sectionSamples, setSectionSamples] = useState([]);
    const [sectionBSamples, setSectionBSamples] = useState([]);       // used for block samples only
    const [showView, setShowView] = useState(undefined);
    const [magInfo, setMagInfo] = useState({});
    const [magTypeInfo, setMagTypeInfo] = useState();
    
    const {magBC} = useParams();
    
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const [showSection, setShowSection] = useState(params.get("section")??"A"); // just get the first section if not specified
    
    const history = useHistory();
    const [cookies,] = useCookies();
    
    useEffect(() => {
        // RECALL -- this runs AFTER all children mount!
        console.debug("ShowMagDetailsPage starting up...");
        window.scrollTo(0,0);
        LIUtils.setBackgroundColor('--li-main-bg-color');
        
        getMagDetails();
        
    }, [magBC,showSection]);  // eslint-disable-line react-hooks/exhaustive-deps
    
    useEffect(() => {
        if(globalState.magTypes.initialized && magInfo) {
            setMagTypeInfo(globalState.magTypes.items.find(item => item.id === magInfo.magazineType));
        }
    }, [magInfo,globalState.magTypes.initialized]);  // eslint-disable-line react-hooks/exhaustive-deps
    
    function handleSampleClicked(sampleItem) {
        const sampleId = sampleItem.id;
        history.push(`/sample_details/${sampleId}`);
    }
    
    function getMagDetails() {
        history.replace(`/mag_details/${magBC}?section=${showSection}`);
        setIsLoading(true);
        ArchiverClientApi.getMagDetailsGivenBarcode(magBC,showSection)
        .then(({magazine,samples}) => {
            setMagInfo(magazine);
            setSectionSamples(samples);
            if(magazine?.holdsSampleType === SAMPLE_TYPE_SLIDE) {
                setShowView("slide");
            }
            else if(magazine?.holdsSampleType === SAMPLE_TYPE_BLOCK) {
                setShowView("block");
                setIsLoadingBSamples(true); // get the B section too
                ArchiverClientApi.getMagDetailsGivenBarcode(magBC,"B")
                .then(({_,samples}) => {
                    setSectionBSamples(samples);
                })
                .finally( () => {
                   setIsLoadingBSamples(false);   // not loading b samples anymore
                });
            }
            else { // show mag view - and get all the rest of the mag sections
                setShowView("general_mag");
                const magSectionsList = generalMagSectionIndexesString(magazine.sections?.length ?? 0, 1);
                if (magSectionsList.length > 0) { // need to get rest of the sections' samples
                    ArchiverClientApi.getMagDetailsGivenBarcode(magBC, magSectionsList)
                    .then((results) => {
                        const remainingSamples:[] = results.samples;
                        const allSamples = samples.concat(remainingSamples); // add in the first section samples (this is hacky).
                        setSectionSamples(allSamples);
                    })
                }
            }
        }) 
        .catch( responseError => ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError))
        .finally( () => {
            setIsLoading(false);
        });
    }
    
    function generalMagSectionIndexesString(numSections,startAtIndex:Number) {
        let indexes = [];
        for(let i=startAtIndex; i<numSections; i++) {
            indexes.push(i);
        }
        return indexes.join(",");
    }
    
    function showSamples(sectionASamples, optionalSectionBSamples) {
        if (isLoading) {
            return <LoadingSpinner medium center/>;
        }
        
        if (showView==="block") {
            return dumpBlockSection(sectionASamples, optionalSectionBSamples);
        }
        else if(showView==="slide") {
            return dumpSlideSection(sectionASamples);
        }
        
        return null; 
    }

    function hasPositionPermissions() {
        return LIUtils.UserCanSeeSampleLocation(cookies);
    }

    function dumpSlideSection(slideSamples) {
        if (!slideSamples || slideSamples.length === 0) {
            return <p className="mx-auto my-5 b-5 text-center">no samples found</p>;
        }
        
        const elementList = [];
        for(let i=0; i<slideSamples.length; i+=2) {
            const itemLeft = slideSamples[i];
            const newElementLeft = <SampleCard key={itemLeft.id} sampleItem={itemLeft} showPosition={hasPositionPermissions()}
                                           needsAttention={itemLeft.hasCaseAndSampleInfo===false}
                                           onClick={() => handleSampleClicked(itemLeft)}/>

            if(i+1 < slideSamples.length) {
                const itemRight = slideSamples[i+1];
                const newElementRight = <SampleCard key={itemRight.id} sampleItem={itemRight} showPosition={hasPositionPermissions()}
                                                 needsAttention={itemRight.hasCaseAndSampleInfo===false}
                                                 onClick={() => handleSampleClicked(itemRight)}/>;
                elementList.push(<div className="row justify-content-center">
                    <div>{newElementLeft}</div>
                    <div>{newElementRight}</div>
                </div>);
            }
            else {
                elementList.push(<div className="row justify-content-center">
                    <div>{newElementLeft}</div>
                    <div className="card li-sample-card dark text-center"></div>
                </div>);
            }
            
        }
        return elementList;
    }

    function dumpBlockSection(sectionASamples, optionalSectionBSamples) {
        
        let outList = [];
        for(let row=1; row<=25; row++) {
            const sLeftSide = sectionASamples?.find(item => item.positionOrder===row);
            if(sLeftSide) {
                outList.push(<div key={"A"+row} className="position-relative">
                    { generateRowNumberBadge(row) }
                                <SampleCard key={sLeftSide.id} sampleItem={sLeftSide} showPosition
                                         needsAttention={sLeftSide.hasCaseAndSampleInfo===false}
                                              onClick={() => handleSampleClicked(sLeftSide)}/>
                            </div>);
            }
            else { // empty
                outList.push(generateBlankForRow(row,0));
            }
            const sRightSide = optionalSectionBSamples?.find(item => item.positionOrder===row);
            if(sRightSide) {
                outList.push(<div key={"B"+row} className="position-relative">
                                <SampleCard key={2*sRightSide.id} sampleItem={sRightSide} showPosition
                                        needsAttention={sRightSide.hasCaseAndSampleInfo===false}
                                        onClick={() => handleSampleClicked(sRightSide)}/>
                            </div>);
            }
            else { // empty
                outList.push(generateBlankForRow(row,1));
            }
        }
        
        return(
            <>
                {isLoadingBSamples && <LoadingSpinner medium center/>}
                <div className="row justify-content-around">
                    <div className="col-12 col-lg-6 text-center badge li-font-medium">Section A</div>
                    <div className="col-12 col-lg-6 text-center badge li-font-medium">Section B</div>
                </div>
                <div className="row justify-content-center">
                    {outList}
                </div>
            </>
        );
    }
    
    function generateRowNumberBadge(row:Number) {
        return <span className="position-absolute badge-pill li-bg-muted li-fg-white" style={{left:"-2.5em"}}>{row}</span> ;
    }
    function generateBlankForRow(row:Number,sectionIndex:Number) {
        return (
            <div key={"blank"+sectionIndex+row} className="position-relative">
                {sectionIndex===0 && generateRowNumberBadge(row)}
                <div className="card li-sample-card dark text-center">-</div>
            </div>
        );
    }
    
    function handleSectionClick(section) {
        setShowSection(section);
    }
    
    function dumpSectionHeader() {
        let sections;
        if (showView !== "slide") {
            return null;
        }
        sections = [
            {name: "A", bg: "li-position-marker-section-a"}, 
            {name: "B", bg: "li-position-marker-section-b"}, 
            {name: "C", bg: "li-position-marker-section-c"}, 
            {name: "D", bg: "li-position-marker-section-d"}
        ];
        
        const sectionList = sections.map((i) => {
            const bgClass = showSection === i.name ? "li-border-dark-thick " + i.bg : "li-pointer " + i.bg;
            return (<li key={i.name} className={"list-inline-item li-font-large badge " + bgClass}
                        onClick={() => handleSectionClick(i.name)}>{i.name}</li>);
        });
        return (<ul className="list-group-horizontal">
                {sectionList}
            </ul>);
    }
    
    function handleGeneralMagDiscard(magBarcode) {
        setDiscardIsBusy(true);
        ArchiverClientApi.sendDiscardMagRequest(magBarcode)
        .then((updatedMagInfo) => {
            setMagInfo(updatedMagInfo);
            setSectionSamples([]);
            Popup.showInfo(`Magazine '${magBarcode}' discarded successfully`, () => {});
        })
        .catch(responseError => ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError))
        .finally( () => {
            setDiscardIsBusy(false);
        });
    }
    
    function handleShowMagHistoryClicked() {
        history.push(`/mag_history/${magBC}`);
    }

    function handleMoveToAnotherLocationClicked() {
        history.push(`/admin/mag_move/${magBC}`);
    }

    return (
        <div className="container">
            <Header title={"Mag Details - "+magBC}/>
            <div className="row mt-3 li-header-row justify-content-between">
                <div>
                    <span style={{verticalAlign:"top"}}><BackButton/></span>
                    <div className="d-inline-block">
                        <div className="">
                            <span className="li-font-huge m-0 p-0">Magazine: </span>
                            <span className="li-font-large li-fg-primary">{magBC??"-"}</span>
                        </div>
                        <div className="m-0 p-0">
                            <span className="li-font-medium ml-0 font-weight-bold">Stored at: </span>
                            <span className="li-font-medium li-fg-slightly-muted">
                                {magInfo?.storageLocation??"(unknown)"}
                                {/* show a move icon if the person has the admin permission */}
                                {LIUtils.UserHasLevel(cookies,"SERVICE") &&
                                    <i className="li-font-medium ml-3 li-fg-primary fa fa-exchange li-pointer" 
                                       data-toggle="tooltip" data-placement="auto" title="Move to another location"
                                       onClick={handleMoveToAnotherLocationClicked}/>}
                            </span>
                        </div>
                    </div>
                </div>
                <div className="align-self-center">
                    <LIButton label="Show Mag History" primary bgClass=""  small onClick={handleShowMagHistoryClicked}/>
                    <ReloadButton/>
                </div>
            </div>

            <SampleLegend useFill={showView==="general_mag"}/>
            
            <div className="row justify-content-around mt-3 mb-3">
                {dumpSectionHeader()}
            </div>
            <div style={{minHeight:"380px"}}>
            {showView!=="general_mag" && showSamples(sectionSamples,sectionBSamples)}
            {showView==="general_mag" && 
                <div className="overflow-auto" style={{height:"auto"}}>
                    <GeneralMagView magTypeInfo={magTypeInfo} magInfo={magInfo} samples={sectionSamples}
                                handleDiscardMag={(magBarcode) => handleGeneralMagDiscard(magBarcode)} 
                                discardIsBusy={discardIsBusy} allowDiscard
                                onSampleClicked={(sample)=>handleSampleClicked(sample)}/>
                </div>
            }
            </div>
        </div>
    );
}

export default MagDetailsPage;